/**
 * Helper functions for generating vCards
 */

/**
 * Constructs a VERSION:3.0 vCard format string from provided field values
 *
 * @param fields Object containing field names as keys and their values
 * @returns A properly formatted vCard string with CR+LF at the end of each line
 */
export const constructVCardString = (fields: Record<string, string>): string => {
  // Start with required vCard elements
  const vCardLines = ['BEGIN:VCARD', 'VERSION:3.0']

  // Map field names to their vCard property equivalents
  // These mappings can be expanded based on the specific fields in your application
  if (fields['FirstName'] || fields['LastName']) {
    const lastName = fields['LastName'] || ''
    const firstName = fields['FirstName'] || ''
    vCardLines.push(`N:${lastName};${firstName};;;`)
  }

  // Full Name (FN) is required in vCard 3.0
  if (fields['FirstName'] && fields['LastName']) {
    vCardLines.push(`FN:${fields['FirstName']} ${fields['LastName']}`)
  } else if (fields['FirstName']) {
    vCardLines.push(`FN:${fields['FirstName']}`)
  } else if (fields['LastName']) {
    vCardLines.push(`FN:${fields['LastName']}`)
  } else if (fields['FullName']) {
    vCardLines.push(`FN:${fields['FullName']}`)
  }

  // Organization
  if (fields['Company'] || fields['Organization']) {
    vCardLines.push(`ORG:${fields['Company'] || fields['Organization'] || ''}`)
  }

  // Job Title
  if (fields['Title'] || fields['JobTitle']) {
    vCardLines.push(`TITLE:${fields['Title'] || fields['JobTitle'] || ''}`)
  }

  // Address
  const addressParts = []

  // Street address - combine AddressLine1 and AddressLine2 if available
  let streetAddress = ''
  if (fields['AddressLine1']) {
    streetAddress = fields['AddressLine1']
    if (fields['AddressLine2']) {
      streetAddress += ', ' + fields['AddressLine2']
    }
  } else if (fields['Address'] || fields['Street']) {
    streetAddress = fields['Address'] || fields['Street'] || ''
  }

  if (streetAddress) addressParts.push(streetAddress)
  if (fields['City']) addressParts.push(fields['City'])
  if (fields['StateProv'] || fields['State']) addressParts.push(fields['StateProv'] || fields['State'] || '')
  if (fields['ZipPostalCode'] || fields['ZipCode'] || fields['PostalCode']) {
    addressParts.push(fields['ZipPostalCode'] || fields['ZipCode'] || fields['PostalCode'] || '')
  }
  if (fields['Country']) addressParts.push(fields['Country'])

  if (addressParts.length > 0) {
    vCardLines.push(`ADR:;;${addressParts.join(';')}`)
  }

  // Phone numbers
  if (fields['Phone'] || fields['WorkPhone'] || fields['Phone2']) {
    vCardLines.push(`TEL;WORK;VOICE:${fields['Phone'] || fields['WorkPhone'] || fields['Phone2'] || ''}`)
  }

  if (fields['Mobile'] || fields['MobilePhone'] || fields['CellPhone'] || fields['Phone1']) {
    vCardLines.push(
      `TEL;CELL:${fields['Mobile'] || fields['MobilePhone'] || fields['CellPhone'] || fields['Phone1'] || ''}`
    )
  }

  if (fields['Fax']) {
    vCardLines.push(`TEL;FAX:${fields['Fax']}`)
  }

  // Email
  if (fields['Email'] || fields['WorkEmail']) {
    vCardLines.push(`EMAIL;WORK;INTERNET:${fields['Email'] || fields['WorkEmail'] || ''}`)
  }

  // Website
  if (fields['Website'] || fields['URL']) {
    vCardLines.push(`URL:${fields['Website'] || fields['URL'] || ''}`)
  }

  // End the vCard
  vCardLines.push('END:VCARD')

  // Join with CR+LF (CRLF) as required by the vCard format
  return vCardLines.join('\r\n')
}

/**
 * Processes template fields and constructs a vCard
 *
 * @param templateFields Array of field name templates in format {FieldName}
 * @param record Optional record containing actual values to replace the placeholders
 * @returns A properly formatted vCard string
 */
export const generateVCardFromTemplateFields = (templateFields: string[], record?: Record<string, string>): string => {
  // Convert template fields like {FirstName} to a record of field names
  const fieldMap: Record<string, string> = {}

  templateFields.forEach((field) => {
    // Extract field name from template format {FieldName}
    const fieldMatch = field.match(/\{([^}]+)\}/)
    if (fieldMatch && fieldMatch[1]) {
      const fieldName = fieldMatch[1]
      // If record is provided, use the actual value from the record
      // Otherwise, keep the placeholder format
      fieldMap[fieldName] = record ? record[fieldName] || '' : `{${fieldName}}`
    }
  })

  return constructVCardString(fieldMap)
}
